import React, { useMemo } from 'react';
import { FlowTable } from '../../components/command/telemetryHud';
import { TelemetryControls } from '../../components/command/useTelemetry';
import { fmtRate } from '../../components/command/telemetryState';
import { SignalChart } from './charts';
import { LiveSection } from './LiveFrame';

function sumSeries(telemetry, slug) {
  const series = telemetry?.series || {};
  const keys = slug ? [slug].filter((s) => series[s]) : Object.keys(series);
  if (!keys.length) return null;
  const first = series[keys[0]];
  const req = first.req.map(() => 0);
  const s5xx = first.req.map(() => 0);
  const p95 = first.req.map(() => 0);
  for (const k of keys) {
    const s = series[k];
    for (let i = 0; i < req.length; i += 1) {
      req[i] += s.req[i] || 0;
      s5xx[i] += s.s5xx[i] || 0;
      p95[i] = Math.max(p95[i], s.p95[i] || 0);
    }
  }
  return { ts: first.ts, req, s5xx, p95 };
}

export default function TrafficBoard({ telemetry, slug = null, controls = null }) {
  const series = useMemo(() => sumSeries(telemetry, slug), [telemetry, slug]);
  const rate = series ? series.s5xx.map((v, i) => (series.req[i] > 0 ? v / series.req[i] : 0)) : [];
  const flows = slug
    ? { ...telemetry, flows: (telemetry.flows || []).filter((f) => f.from === slug || f.to === slug) }
    : telemetry;
  const total = series ? series.req.reduce((n, v) => n + v, 0) : 0;
  return (
    <>
      <LiveSection title="Traffic" meta={telemetry?.source === 'sim' ? `simulated · ${telemetry.window?.label || ''}` : (telemetry?.window?.label || '')}>
        {controls ? <div style={{ marginBottom: 12 }}><TelemetryControls controls={controls} /></div> : null}
        {!series && <p className="cs-help">No traffic data yet. The edge access log rolls up once the host agent is on this pipeline.</p>}
        {series && (
          <>
            <p className="cs-help" style={{ marginTop: 0 }}>{fmtRate(series.req[series.req.length - 1] || 0)} now · {Math.round(total).toLocaleString()} requests in this window{slug ? '' : ' · summed across apps'}.</p>
            <SignalChart label="Requests / min" values={series.req} format={(v) => fmtRate(v)} />
            <SignalChart label="5xx rate" values={rate} format={(v) => `${(v * 100).toFixed(2)}%`} />
            <SignalChart label="Worst p95" values={series.p95} format={(v) => `${Math.round(v)} ms`} />
          </>
        )}
      </LiveSection>
      <LiveSection title="Inter-app flows">
        <FlowTable t={flows} />
      </LiveSection>
    </>
  );
}
